import {Component, EventEmitter, Input, OnInit, OnDestroy, Output} from '@angular/core';
import { TVDService } from 'src/app/features/total-value-delivery/services/tvd.service';
import { custTile, eroiIds, tvdData, TvdImpactCategory, TvdValuePillar, ValuePillarToImpactCategoryMapping } from '../../types/tvd-types';
import { FiltersService } from 'src/app/core/services/filters.service';
import { Subscription } from 'rxjs';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { eroiService } from '../../services/eroi-metric.service';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';

// TODO : This seems like a repeat component of the CustomTileEntryPanelComponent with a different action, 
// consider refactoring to reuse code and avoid duplication
@Component({
    selector: 'app-custom-tile-edit-panel',
    templateUrl: './custom-tile-edit-panel.component.html',
    styleUrls: ['./custom-tile-edit-panel.component.scss'],
    standalone: false
})
export class CustomTileEditPanelComponent implements OnInit, OnDestroy {
  filters: ContractModel | undefined;
  private filtersSubscription: Subscription | undefined;
  private currencySubscription: Subscription | undefined;


  @Output() hideCustEditTilePanel = new EventEmitter<string>();
  @Output() customTileEdited = new EventEmitter<custTile>();
  
  inputTitle: string = "";
  inputCost: string = "";
  inputValue: string = "";
  inputUnit: string = "";
  inputValuePillar: string = "";
  inputImpactCategory : string = ""
  costCurrencyCode: string = "USD";
  iconPath: string = "";

  //Map the enum to arrays for dropdown options
  TvdValuePillars = Object.values(TvdValuePillar);
  TvdImpactCategories : TvdImpactCategory[] = Object.values(TvdImpactCategory);
  ValuePillarToImpactCategoryMapping = ValuePillarToImpactCategoryMapping;
  

  isPestDivision: boolean = false;

  //Passes in the required dictionary to keep track of the custom tiles
  @Input() customTilesDict : Map<string, custTile> | undefined;

  //Passes in the parent component's selectedItemsDict to keep selection state in sync
  @Input() selectedItemsDict : Map<string, [data:tvdData, isSelected:boolean, isAddedToPres:boolean]> | undefined;

  //Direct access to the service's dictionary
  serviceCustomTileDict : Map<string, custTile> | undefined;

  constructor(
      private tvdService: TVDService, 
      private eroiMetricService : eroiService, 
      private filtersService: FiltersService, 
      private currencyUomService: CurrencyUomService
    ) {
    this.inputTitle = this.tvdService.currCustTitle || '';
    this.inputCost = this.tvdService.currCustCost || '';
    this.inputUnit = this.tvdService.currCustUnit || '';
    this.inputValue = this.tvdService.currCustValue || '';
    this.inputValuePillar = this.tvdService.currCustValuePillar || '';
    this.inputImpactCategory = this.tvdService.currCustImpactCategory || '';

    this.serviceCustomTileDict = tvdService.custTileDict;
    this.UpdateAvailableImpactCategoriesForValuePillar(this.inputValuePillar);
    this.iconPath = this.eroiMetricService.mapImpactCategoryToIconPath(this.inputImpactCategory);
  }

  //frontend selectedItemsDict will have issue with any of the keys,
  //backend will throw error if there is a repeating title
  private get idAndTitleMap(): {[index:string]:string} {
    // TODO: Refactor to reuse PestTileMap and StandardTileMap from tvd-types.ts to avoid duplication (DRY principle)
    // For Pest division, only include Pest-specific entries plus custom-tile
    if (this.isPestDivision) {
      return {
        [eroiIds.NOCHARGEADDITIONALSERVICES]: "No Charge Additional Services",
        [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE]: "General Contractor Cost Avoidance",
        [eroiIds.SETUPSERVICES]: "Set-up Services",
        [eroiIds.PESTPREVENTIONFINDINGS]: "Pest Prevention Findings",
        [eroiIds.ROOMNIGHTSSAVED]: "Room Nights Saved",
        [eroiIds.FURNITUREPRESERVATIONASSETS]: "Furniture Preservation (Assets)",
        [eroiIds.FURNITUREPRESERVATIONENERGY]: "Furniture Preservation (Energy)",
        "custom-tile": ""
      };
    }

    return {
      [eroiIds.WATER] : "Water",
      "energy": "Energy",
      "greenhouse-gases": "Greenhouse Gases",
      "plastic-waste": "Plastic Waste",
      "productivity": "Productivity",
      "assets": "Assets",
      "cardboard-waste": "Cardboard Waste",
      "employee-safety": "Employee Safety",
      "cost-avoidance": "Cost Avoidance",
      "portfolio-reduction": "Portfolio Reduction",
      "custom-tile": ""
    };
  }

  // Getter methods to determine labels based on division
  get costLabel(): string {
    return this.isPestDivision ? 'Total TVD $ Value' : 'Cost';
  }

  get valueLabel(): string {
    return this.isPestDivision ? 'Total Number of Units' : 'Value';
  }

  ngOnInit(): void {
    this.filtersSubscription = this.filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    if(this.filters) {
      this.costCurrencyCode = this.currencyUomService.getDisplayedCurrency();
      this.isPestDivision = this.filters.division == 'Pest';
    }

    this.currencySubscription = this.currencyUomService.displayedCurrency$.subscribe(() => {
      this.costCurrencyCode = this.currencyUomService.getDisplayedCurrency();
    });
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.currencySubscription?.unsubscribe();
  }

  clickCloseLink() {
    this.hideCustEditTilePanel.emit();
  }

  keyEvent(event: { keyCode: number; preventDefault: () => void; })
  {
    if(event.keyCode === 13)
    {
      event.preventDefault();
      this.clickSaveButton();
    }

    if(event.keyCode === 27)
    {
      event.preventDefault();
      this.clickCloseLink();
    }
  }

  // As user selects a Value Pillar, update the Impact Categories based on selected Value Pillar
  onValuePillarChange(): void {
    this.UpdateAvailableImpactCategoriesForValuePillar(this.inputValuePillar);    
    this.inputImpactCategory = ''; // Reset selected Impact Category
  }

  // As user selects an Impact Category, update the Value Pillar selected based on selected Impact Category
  onImpactCategoryChange(): void {
    let valuePillar : TvdValuePillar | undefined = undefined;
    for (const [pillar, impactCategories] of this.ValuePillarToImpactCategoryMapping.entries()) {
      //foreach set of impact categories, if the selected impact category is in the set, return the value pillar
      if (impactCategories.includes(this.inputImpactCategory as TvdImpactCategory)) 
      {
        valuePillar = pillar;
        break;
      }
    }

    // Update icon path based on selected impact category
    this.iconPath = this.eroiMetricService.mapImpactCategoryToIconPath(this.inputImpactCategory);

    // Update the selection of the value pillar dropdown
    this.inputValuePillar = valuePillar ?? "";

    //also update the available options for the Imapct Categories for the correct Value Pillar
    this.UpdateAvailableImpactCategoriesForValuePillar(this.inputValuePillar);
  }

  // @ts-ignore
  clickSaveButton() {
    const title = this.inputTitle!;
    let value = this.inputValue!;
    let cost = this.inputCost!;
    const unit = this.inputUnit!;
    const valuePillar = this.inputValuePillar;
    const impactCategory = this.inputImpactCategory;

    //Keep track of the items "previous" title, because that is used as a key
    let prevTitle = this.tvdService.currCustTitle;
    //Update the corresponding values
    this.tvdService.currCustTitle = title;
    this.tvdService.currCustCost = cost;
    this.tvdService.currCustUnit = unit;
    this.tvdService.currCustValue = value;
    this.tvdService.currCustValuePillar = valuePillar;
    this.tvdService.currCustImpactCategory = impactCategory;

    // check for blank
    if (title === undefined || title== "") {
      window.alert("The title field cannot be empty.");
      return false;

    } else if (cost === undefined || cost== "") {
      window.alert("The cost field cannot be empty.");
      return false;

    } else if (value === undefined || value== "") {
      window.alert("The value field cannot be empty.");
      return false;

    } else if (unit === undefined || unit== "") {
      window.alert("The unit field cannot be empty.");
      return false;

    } else if(!valuePillar){
      window.alert("The Value Pillar field cannot be empty.")
      return false;

    } else if(!impactCategory){
      window.alert("The Impact Category field cannot be empty.")
      return false;

    } else if (title.includes("_")) { //will interfere with writing to ppwt template
      window.alert("Title cannot include an underscore.");
      return false;
    }
      else if(this.customTilesDict?.get(title) && title != prevTitle){
      window.alert("Duplicate title found, please enter a unique title.");
      return false;
    } else if(Object.keys(this.idAndTitleMap).includes(title) ||  Object.values(this.idAndTitleMap).includes(title)){
      window.alert(`Duplicate title id found (${title}), try adding a special character or suffix to ensure uniqueness.`);
      return false;
    } else{
      //No non-numbers
      for(let i=0; i<cost.length; i++){
        if(cost[i] != '1' && cost[i] != '2' && cost[i] != '3' && cost[i] != '4'
        && cost[i] != '5' && cost[i] != '6' && cost[i] != '7' && cost[i] != '8'
        && cost[i] != '9' && cost[i] != '0' && cost[i] != ','){
          window.alert("The cost field may only contain numerical characters.");
          return false;
        }
      }
      for(let i=0; i<value.length; i++){
        if(value[i] != '1' && value[i] != '2' && value[i] != '3' && value[i] != '4'
        && value[i] != '5' && value[i] != '6' && value[i] != '7' && value[i] != '8'
        && value[i] != '9' && value[i] != '0' && value[i] != ','){
          window.alert("The value field may only contain numerical characters.");
          return false;
        }
      }

      //Reformat the number string if it has more than 3 elements to include commas
      //First, remove all commas so this works
      cost=cost.replace(/,/g,'');
      value=value.replace(/,/g,'');

      if(cost.length > 3)
      {
        cost = (Number(cost)).toLocaleString();
      }
      if(value.length > 3)
      {
        value = (Number(value)).toLocaleString();
      }

      //Check if this item was already selected.
      //If it was, then change the value sent to ppt directly
      if(this.tvdService.addedMetrics){
        let tileItem = this.tvdService.addedMetrics!.find(tile => tile.Title == prevTitle);
        if (tileItem != undefined) {
          tileItem.Title = title;
          tileItem.Value = value;
          tileItem.ValueUnit = unit;
          tileItem.Cost = cost;
          tileItem.ValuePillar = valuePillar.replace(/\s+/g, ''); //remove spaces so the strings can map to the enums in API
          tileItem.ImpactCategory = impactCategory.replace(/\s+/g, ''); //remove spaces so enums can map to the enums in API
        }
      }

      let editTile: custTile | undefined;
      //Access the item desired by its original key - its previous title
      if(this.serviceCustomTileDict && prevTitle){
         editTile = this.serviceCustomTileDict.get(prevTitle);

        //Update the object
        if(editTile!.title !== undefined) {
          editTile!.title = title;
        }
        if(editTile!.cost !== undefined) {
          editTile!.cost = cost;
        }
        if(editTile!.metricValue !== undefined) {
          editTile!.metricValue = value;
        }
        if(editTile!.unit !== undefined) {
         editTile!.unit = unit;
        }
        if(editTile!.valuePillar !== undefined) {
         editTile!.valuePillar = valuePillar as TvdValuePillar;
        }
        if(editTile!.impactCategory !== undefined) {
         editTile!.impactCategory = impactCategory as TvdImpactCategory;
        }
      }

      //Now set this item where it belongs in this.customTilesDict to update page
      //if title is different, delete old one
      if (title !== prevTitle) {
        this.customTilesDict!.delete(prevTitle as string);
      }
      //set new value for dict
      this.customTilesDict!.set(title, editTile as custTile);

      //update custom tile observable so that tile updates
      this.tvdService.updateCustomTileDict(this.customTilesDict!);

      const updatedData: tvdData = {
        monetaryContribution: editTile?.cost,
        valueContribution: editTile?.metricValue as (string | undefined)
      };

      if(this.tvdService.selectedItemsDict?.get(prevTitle as string) != undefined)
      {
        //get tile selection state and inject it into the new tile object
        let tileSelectionState = this.tvdService.selectedItemsDict?.get(prevTitle as string);
        let newSelectionState : [data: tvdData, isSelected: boolean, isAddedToPres: boolean] = [
          updatedData,
          tileSelectionState![1],
          tileSelectionState![2]
        ]

        this.tvdService.selectedItemsDict?.delete(prevTitle as string); //delete old item
        this.tvdService.selectedItemsDict?.set(title, newSelectionState); //add in new tile
      }

      // Also sync the parent component's selectedItemsDict to preserve selection state
      // and remove stale keys after a rename.
      if (this.selectedItemsDict) {
        const prevEntry = this.selectedItemsDict.get(prevTitle as string);
        const isSelected = prevEntry?.[1] ?? false;
        const isAddedToPres = prevEntry?.[2] ?? false;
        if (title !== prevTitle) {
          this.selectedItemsDict.delete(prevTitle as string);
        }
        this.selectedItemsDict.set(title, [updatedData, isSelected, isAddedToPres]);
      }
      this.customTileEdited.emit(editTile);
    }
    this.clickCloseLink();
  }

  // Update the Impact Categories based on selected Value Pillar
  private UpdateAvailableImpactCategoriesForValuePillar(valuePillar : string | TvdValuePillar){
    let validImpactCategories = this.ValuePillarToImpactCategoryMapping.get(valuePillar as TvdValuePillar);
    this.TvdImpactCategories = validImpactCategories ?? Object.values(TvdImpactCategory);
  }
}
